import React, { useState, useEffect, useCallback, useRef } from 'react';
import "@/App.css";
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { ticketService } from './services/api';
import { StatsCards } from './components/dashboard/StatsCards';
import { TicketTable } from './components/tickets/TicketTable';
import { EmailImportModal } from './components/tickets/EmailImportModal';
import { TicketFormModal } from './components/tickets/TicketFormModal';
import { BulkImportModal } from './components/tickets/BulkImportModal';
import { SmartImportModal } from './components/tickets/SmartImportModal';
import { GoogleSheetsImportModal } from './components/modals/GoogleSheetsImportModal';
import { InboxModal } from './components/email/InboxModal';
import { PsgMailModal } from './components/email/PsgMailModal';
import { DoublonsModal } from './components/tickets/DoublonsModal';
import { AntiSingleModal } from './components/tickets/AntiSingleModal';
import { AttribuerVenteModal } from './components/tickets/AttribuerVenteModal';
import { SpecialAttributionModal } from './components/tickets/SpecialAttributionModal';
import { RefCheckModal } from './components/tickets/RefCheckModal';
import { ProofUploadModal } from './components/tickets/ProofUploadModal';
import { ProofEnvoiModal } from './components/tickets/ProofEnvoiModal';
import { BulkValidateModal } from './components/modals/BulkValidateModal';
import { MatcherFilterModal } from './components/modals/MatcherFilterModal';
import ErrorBoundary from './components/ErrorBoundary';
// ProofsView removed - feature disabled
import { DeltaView } from './components/dashboard/DeltaView';
import { LogView } from './components/dashboard/LogView';
import { StadiumMappingView } from './components/dashboard/StadiumMappingView';
import { BackupsView } from './components/dashboard/BackupsView';
import { MarketEvolutionView } from './components/dashboard/MarketEvolutionView';
import { WorldCupGroupsView } from './components/dashboard/WorldCupGroupsView';
import { RGMatchesView } from './components/dashboard/RGMatchesView';
import AccountsView from './components/tickets/AccountsView';
import { LoginPage } from './components/auth/LoginPage';
import { Button } from './components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './components/ui/dropdown-menu';
import { 
  ShoppingCart, 
  TrendingUp, 
  BarChart3, 
  FileText,
  Mail,
  Plus,
  RefreshCw,
  Zap,
  FileSpreadsheet,
  ChevronDown,
  LayoutGrid,
  Inbox,
  LogOut,
  Download,
  AlertTriangle,
  AlertCircle,
  ArrowRight,
  Play,
  Pause,
  Clock,
  Settings,
  X,
  Filter,
  History,
  RotateCcw,
  User,
  Search,
  Sun,
  Moon,
  Bug,
  CheckCircle,
  Trash2,
  Archive
} from 'lucide-react';

// Logo YAS - Grande taille (150px) avec effet hover
const YASLogo = () => (
  <img 
    src="/litepremium_logo.png" 
    alt="Lite Premium"
    className="cursor-pointer transition-all duration-300 hover:scale-105"
    style={{ 
      height: '150px', 
      width: 'auto',
      filter: 'drop-shadow(0 0 20px rgba(212, 175, 55, 0.5))'
    }}
    onMouseEnter={(e) => e.target.style.filter = 'drop-shadow(0 0 40px rgba(212, 175, 55, 1)) drop-shadow(0 0 70px rgba(212, 175, 55, 0.8))'}
    onMouseLeave={(e) => e.target.style.filter = 'drop-shadow(0 0 20px rgba(212, 175, 55, 0.5))'}
  />
);

// Fonction utilitaire pour normaliser les accents (pour la recherche insensible aux accents)
const normalizeAccents = (str) => {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};

function App() {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [userName, setUserName] = useState('');
  const [authChecking, setAuthChecking] = useState(true);
  
  // Theme state (dark by default)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('yas_theme');
    return saved ? saved === 'dark' : true; // Default to dark
  });
  
  // Apply theme on change
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.remove('light-mode');
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
      document.documentElement.classList.add('light-mode');
    }
    localStorage.setItem('yas_theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);
  
  const toggleTheme = () => setIsDarkMode(!isDarkMode);

  // Check existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('yas_token');
    const name = localStorage.getItem('yas_user');
    if (token) {
      // Verify token is still valid
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify?token=${token}`, {
        timeout: 10000 // 10 second timeout
      })
        .then(res => res.json())
        .then(data => {
          if (data.valid) {
            setIsAuthenticated(true);
            setAuthToken(token);
            setUserName(data.name || name);
          } else if (data.reason === "Session expirée") {
            // Only logout if session is explicitly expired
            console.log("Session expirée, déconnexion");
            localStorage.removeItem('yas_token');
            localStorage.removeItem('yas_user');
          } else {
            // For any other reason (server restart, etc), keep user logged in
            // The session will be recreated on next login if needed
            console.log("Token non reconnu mais on garde la session locale");
            setIsAuthenticated(true);
            setAuthToken(token);
            setUserName(name || '');
          }
        })
        .catch((error) => {
          // If server error or network issue, KEEP user logged in
          console.log("Erreur vérification token, session locale maintenue:", error);
          setIsAuthenticated(true);
          setAuthToken(token);
          setUserName(name || '');
        })
        .finally(() => setAuthChecking(false));
    } else {
      setAuthChecking(false);
    }
  }, []);

  const handleLogin = (token, name) => {
    setAuthToken(token);
    setUserName(name);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('yas_token');
    localStorage.removeItem('yas_user');
    setIsAuthenticated(false);
    setAuthToken(null);
    setUserName('');
  };

  // App state
  const [activeTab, setActiveTab] = useState('tout');
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [delta, setDelta] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showSmartImportModal, setShowSmartImportModal] = useState(false);
  const [showGoogleSheetsModal, setShowGoogleSheetsModal] = useState(false);
  const [recapDeltaFilter, setRecapDeltaFilter] = useState(null); // Filtre depuis RECAP DELTA
  const [showInboxModal, setShowInboxModal] = useState(false);
  const [showPsgModal, setShowPsgModal] = useState(false);
  const [showDoublonsModal, setShowDoublonsModal] = useState(false);
  const [showBulkValidateModal, setShowBulkValidateModal] = useState(false);
  const [showProofModal, setShowProofModal] = useState(false);
  const [proofTicket, setProofTicket] = useState(null);
  const [showProofEnvoiModal, setShowProofEnvoiModal] = useState(false);
  const [proofEnvoiTicket, setProofEnvoiTicket] = useState(null);
  const [showAntiSingleModal, setShowAntiSingleModal] = useState(false);
  const [showAttribuerVenteModal, setShowAttribuerVenteModal] = useState(false);
  const [showSpecialAttributionModal, setShowSpecialAttributionModal] = useState(false);
  const [showRefCheckModal, setShowRefCheckModal] = useState(false);
  const [selectedVenteForAttribution, setSelectedVenteForAttribution] = useState(null);
  const [editingTicket, setEditingTicket] = useState(null);
  const [selectedAccountFilter, setSelectedAccountFilter] = useState(null); // Filtre par compte email
  const [statusFilter, setStatusFilter] = useState(null);
  
  // Projet courant - persisté dans localStorage
  const [currentProject, setCurrentProject] = useState(() => {
    const saved = localStorage.getItem('yas_current_project');
    return saved || 'WORLD CUP 2026';
  });
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const [showProjectChangeConfirm, setShowProjectChangeConfirm] = useState(false);
  const [pendingProjectChange, setPendingProjectChange] = useState(null);
  
  // Sauvegarder le projet dans localStorage quand il change
  useEffect(() => {
    if (currentProject) {
      localStorage.setItem('yas_current_project', currentProject);
    }
  }, [currentProject]);
  
  // Fonction pour changer de projet avec confirmation
  const requestProjectChange = (newProject) => {
    if (newProject === currentProject) {
      setShowProjectMenu(false);
      return;
    }
    setPendingProjectChange(newProject);
    setShowProjectChangeConfirm(true);
    setShowProjectMenu(false);
  };
  
  const confirmProjectChange = () => {
    if (pendingProjectChange) {
      setCurrentProject(pendingProjectChange);
      setPendingProjectChange(null);
    }
    setShowProjectChangeConfirm(false);
  };
  
  const cancelProjectChange = () => {
    setPendingProjectChange(null);
    setShowProjectChangeConfirm(false);
  };
  
  // Tickets filtrés par TicketTable (pour export avec filtres)
  const [tableFilteredTickets, setTableFilteredTickets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  
  // Global search term - persists across tabs
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  
  // Delta filter state - for filtering from DELTA view
  const [deltaFilter, setDeltaFilter] = useState(null); // { matchNum, category, viewType, eventName }

  // Quand le deltaFilter (RECAP DELTA) est activé, vider la barre de recherche
  useEffect(() => {
    if (deltaFilter) {
      // Toujours vider la barre de recherche quand deltaFilter est activé
      setGlobalSearchTerm('');
    }
  }, [deltaFilter]); // Ne dépend QUE de deltaFilter, pas de globalSearchTerm

  // Auto Matcher state
  const [autoMatcherEnabled, setAutoMatcherEnabled] = useState(false);
  const [autoMatcherInterval, setAutoMatcherInterval] = useState(60); // seconds
  const [lastMatcherRun, setLastMatcherRun] = useState(null);
  const [matcherRunning, setMatcherRunning] = useState(false);
  const [showMatcherFilterModal, setShowMatcherFilterModal] = useState(false);

  // Auto Email Sync state
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [autoSyncInterval, setAutoSyncInterval] = useState(60); // seconds (default 1 minute)
  const [lastSyncRun, setLastSyncRun] = useState(null);
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncStats, setSyncStats] = useState({ newTickets: 0, updatedTickets: 0 });

  // Server-side Background Sync state (24/7)
  const [serverSyncStatus, setServerSyncStatus] = useState(null);
  const [serverSyncLoading, setServerSyncLoading] = useState(false);

  // Filter values from backend (for complete dropdown lists)
  const [filterValues, setFilterValues] = useState(null);

  // Auto-refresh state - rafraîchit automatiquement quand de nouveaux tickets arrivent
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [lastTicketCount, setLastTicketCount] = useState(0);
  const autoRefreshIntervalRef = useRef(null);
  
  // Flag pour savoir si c'est un refresh silencieux (ne pas réinitialiser les filtres/page)
  const [isSilentRefresh, setIsSilentRefresh] = useState(false);

  // Charger les projets depuis le backend
  const fetchProjects = useCallback(async () => {
    try {
      setProjectsLoading(true);
      const response = await ticketService.getProjects();
      const projectNames = response.projects.map(p => p.name);
      setProjects(projectNames);
      
      // NE PAS changer automatiquement de projet !
      // On garde le projet actuel même s'il n'est pas dans la liste
      // (il sera créé automatiquement si nécessaire)
    } catch (error) {
      console.error('Error fetching projects:', error);
      // Fallback vers des projets par défaut
      setProjects(['WORLD CUP 2026', 'PSG', 'FFR', 'ROLAND GARROS']);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  // Ajouter un nouveau projet
  const handleAddProject = async () => {
    const name = window.prompt('Nom du nouveau projet :');
    if (name && name.trim()) {
      try {
        await ticketService.createProject(name.trim());
        toast.success(`Projet "${name.trim().toUpperCase()}" créé !`);
        await fetchProjects();
        // Demander confirmation avant de changer
        requestProjectChange(name.trim().toUpperCase());
        setShowProjectMenu(false);
      } catch (error) {
        console.error('Error creating project:', error);
        toast.error(error.response?.data?.detail || 'Erreur lors de la création du projet');
      }
    }
  };

  // Supprimer un projet avec DOUBLE VÉRIFICATION
  const handleDeleteProject = async (projectName) => {
    if (projectName === currentProject) {
      toast.error('Impossible de supprimer le projet actif. Changez de projet d\'abord.');
      return;
    }
    
    // Récupérer les stats du projet
    let ticketCount = 0;
    try {
      const stats = await ticketService.getProjectStats(projectName);
      ticketCount = stats.total_tickets || 0;
    } catch (e) {
      console.error('Error getting project stats:', e);
    }
    
    // ÉTAPE 1: Première confirmation
    const confirmMsg = `⚠️ ATTENTION ⚠️\n\nVoulez-vous vraiment supprimer le projet "${projectName}" ?\n\n📊 Ce projet contient ${ticketCount} ticket(s).\n\n❌ Cette action est IRRÉVERSIBLE !`;
    if (!window.confirm(confirmMsg)) return;
    
    // ÉTAPE 2: Double vérification - Taper le nom du projet
    const typedName = window.prompt(
      `🔐 DOUBLE VÉRIFICATION\n\nPour confirmer la suppression, tapez exactement le nom du projet :\n\n"${projectName}"`
    );
    
    if (!typedName || typedName.trim().toUpperCase() !== projectName.toUpperCase()) {
      toast.error('Le nom tapé ne correspond pas. Suppression annulée.');
      return;
    }
    
    try {
      const result = await ticketService.deleteProject(projectName, true);
      toast.success(`✅ Projet "${projectName}" supprimé définitivement (${result.deleted_tickets} tickets supprimés)`);
      await fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Charger les projets au démarrage
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Load stats, delta, logs, and filter values first (fast)
      const [statsData, deltaData, logsData, filterValuesData] = await Promise.all([
        ticketService.getStats(currentProject),
        ticketService.getDelta(currentProject),
        ticketService.getLogs(100),
        ticketService.getFilterValues(currentProject),
      ]);
      setStats(statsData);
      setDelta(deltaData);
      setLogs(logsData);
      setFilterValues(filterValuesData);
      
      // Load ACHAT and VENTE separately to ensure both types are loaded
      // This fixes the issue where only one type would load with a limit
      const [achatData, venteData] = await Promise.all([
        ticketService.getTickets(currentProject, { type: 'ACHAT', limit: 8000 }),
        ticketService.getTickets(currentProject, { type: 'VENTE', limit: 5000 }),
      ]);
      
      // Combine both types
      setTickets([...achatData, ...venteData]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [currentProject]);

  // Load all tickets in background after initial load
  const loadAllTickets = useCallback(async () => {
    try {
      // Load ALL tickets - necessary for correct display of all tabs
      const allTickets = await ticketService.getTickets(currentProject);
      setTickets(allTickets);
    } catch (error) {
      // Don't show error toast for aborted requests (happens during navigation)
      if (error?.name !== 'AbortError' && error?.code !== 'ERR_CANCELED') {
        console.error('Error loading all tickets:', error);
        // Silent fail - initial data is already loaded
      }
    }
  }, [currentProject]);

  // État pour le changement de projet (pour forcer un rechargement propre)
  const [projectSwitching, setProjectSwitching] = useState(false);
  const previousProjectRef = useRef(currentProject);

  // Effet pour nettoyer les données quand on change de projet
  useEffect(() => {
    if (previousProjectRef.current !== currentProject) {
      console.log(`🔄 Changement de projet: ${previousProjectRef.current} → ${currentProject}`);
      
      // Marquer qu'on est en train de changer de projet
      setProjectSwitching(true);
      
      // EFFACER TOUTES LES DONNÉES IMMÉDIATEMENT
      setTickets([]);
      setStats(null);
      setDelta([]);
      setLogs([]);
      setFilterValues(null);
      setTableFilteredTickets([]);
      setLastTicketCount(0);
      
      // Mettre à jour la référence
      previousProjectRef.current = currentProject;
      
      // Petit délai pour s'assurer que l'UI est nettoyée avant de recharger
      setTimeout(() => {
        setProjectSwitching(false);
      }, 100);
    }
  }, [currentProject]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load all tickets after initial render
  useEffect(() => {
    if (!loading && tickets.length > 0) {
      const timer = setTimeout(() => {
        loadAllTickets();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [loading, loadAllTickets]);

  // ==================== AUTO-REFRESH ====================
  // Vérifie toutes les 30 secondes si de nouveaux tickets sont arrivés
  // et rafraîchit automatiquement l'interface SANS réinitialiser les filtres/page
  useEffect(() => {
    if (!isAuthenticated || !autoRefreshEnabled) return;

    const checkForNewTickets = async () => {
      try {
        // Récupérer juste les stats (rapide) pour vérifier le nombre de tickets
        const newStats = await ticketService.getStats(currentProject);
        const newTotal = (newStats?.achats?.total || 0) + (newStats?.ventes?.total || 0);
        
        // Si le nombre de tickets a changé, rafraîchir les données
        if (lastTicketCount > 0 && newTotal !== lastTicketCount) {
          console.log(`🔄 Auto-refresh: ${lastTicketCount} → ${newTotal} tickets`);
          
          // Marquer comme refresh silencieux
          setIsSilentRefresh(true);
          
          // Mise à jour silencieuse des stats, delta, logs
          const [statsData, deltaData, logsData] = await Promise.all([
            ticketService.getStats(currentProject),
            ticketService.getDelta(currentProject),
            ticketService.getLogs(100),
          ]);
          setStats(statsData);
          setDelta(deltaData);
          setLogs(logsData);
          
          // Charger les nouveaux tickets
          const [achatData, venteData] = await Promise.all([
            ticketService.getTickets(currentProject, { type: 'ACHAT', limit: 8000 }),
            ticketService.getTickets(currentProject, { type: 'VENTE', limit: 5000 }),
          ]);
          
          // Mise à jour intelligente: garder les IDs existants à leur place, ajouter les nouveaux
          const newTickets = [...achatData, ...venteData];
          setTickets(prevTickets => {
            // Créer un map des nouveaux tickets par ID
            const newTicketsMap = new Map(newTickets.map(t => [t.id, t]));
            
            // Mettre à jour les tickets existants et ajouter les nouveaux
            const existingIds = new Set(prevTickets.map(t => t.id));
            
            // Mettre à jour les tickets existants avec les nouvelles données
            const updatedExisting = prevTickets.map(t => newTicketsMap.get(t.id) || t);
            
            // Trouver les nouveaux tickets (pas dans existingIds)
            const brandNewTickets = newTickets.filter(t => !existingIds.has(t.id));
            
            // Retourner les existants mis à jour + les nouveaux à la fin
            return [...updatedExisting, ...brandNewTickets];
          });
          
          // Notification discrète
          const diff = newTotal - lastTicketCount;
          if (diff > 0) {
            toast.success(`${diff} nouveau(x) ticket(s) ajouté(s)`, { duration: 3000 });
          } else if (diff < 0) {
            toast.info(`${Math.abs(diff)} ticket(s) supprimé(s)`, { duration: 3000 });
          }
          
          // Fin du refresh silencieux
          setTimeout(() => setIsSilentRefresh(false), 100);
        }
        
        setLastTicketCount(newTotal);
      } catch (error) {
        console.error('Auto-refresh error:', error);
        setIsSilentRefresh(false);
      }
    };

    // Vérifier toutes les 30 secondes
    autoRefreshIntervalRef.current = setInterval(checkForNewTickets, 30000);
    
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    };
  }, [isAuthenticated, autoRefreshEnabled, currentProject, lastTicketCount]);

  // Mettre à jour lastTicketCount quand les données sont chargées initialement
  useEffect(() => {
    if (stats) {
      const total = (stats?.achats?.total || 0) + (stats?.ventes?.total || 0);
      setLastTicketCount(total);
    }
  }, [stats]);

  const handleImport = async (content, subject, fromEmail) => {
    const result = await ticketService.parseEmail(content, subject, fromEmail);
    toast.success(`${result.tickets_created} billet(s) importé(s)`);
    fetchData();
    return result;
  };

  const handleBulkImport = async (tickets) => {
    const result = await ticketService.bulkImport(tickets, currentProject);
    // Show detailed import result
    const messages = [];
    if (result.created > 0) messages.push(`${result.created} créé(s)`);
    if (result.updated > 0) messages.push(`${result.updated} mis à jour`);
    if (result.skipped > 0) messages.push(`${result.skipped} ignoré(s)`);
    const message = messages.length > 0 ? messages.join(', ') : 'Aucun changement';
    toast.success(`Import terminé: ${message}`);
    fetchData();
    return result;
  };

  const handleInboxProcess = async (emailContent) => {
    // Process email content and create tickets
    const result = await ticketService.parseEmail(emailContent, '', '', currentProject);
    fetchData();
    return result;
  };

  const handleSaveTicket = async (data, ticketId) => {
    try {
      if (ticketId) {
        await ticketService.updateTicket(ticketId, data);
        toast.success('Billet mis à jour');
      } else {
        // For new tickets, add the current project
        await ticketService.createTicket({ ...data, project: currentProject });
        toast.success('Billet créé');
      }
      await fetchData();  // ✅ Attendre le rechargement
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      throw error;
    }
  };

  const handleUnmatch = async (ticketId) => {
    try {
      await ticketService.unmatchTicket(ticketId);
      toast.success('Ticket déassocié');
      await fetchData();  // ✅ Attendre le rechargement
    } catch (error) {
      console.error('Unmatch error:', error);
      toast.error(`Erreur: ${error.response?.data?.detail || error.message || 'Erreur inconnue'}`);
    }
  };

  const handleDelete = async (ticketId) => {
    if (!window.confirm('Supprimer ce billet ?')) return;
    try {
      await ticketService.deleteTicket(ticketId);
      toast.success('Billet supprimé');
      await fetchData();  // ✅ Attendre le rechargement
    } catch (error) {
      // Extract error message from API response
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(errorMessage);
    }
  };

  const handleBulkDelete = async (ticketIds, forceDelete = false) => {
    try {
      const result = await ticketService.bulkDeleteTickets(ticketIds, false, null, currentProject, forceDelete);
      
      // Si des tickets attribués ont été détectés et qu'on n'a pas forcé la suppression
      if (result.needs_confirmation) {
        // Demander confirmation à l'utilisateur
        const confirmDelete = window.confirm(
          `⚠️ ATTENTION ⚠️\n\n${result.message}\n\nCliquez OK pour supprimer quand même, ou Annuler pour garder les tickets.`
        );
        
        if (confirmDelete) {
          // Relancer avec force_delete = true
          const forceResult = await ticketService.bulkDeleteTickets(ticketIds, false, null, currentProject, true);
          toast.success(`${forceResult.deleted} billet(s) supprimé(s) (suppression forcée)`);
          await fetchData();
        } else {
          toast.info('Suppression annulée');
        }
        return;
      }
      
      toast.success(`${result.deleted} billet(s) supprimé(s)`);
      await fetchData();  // ✅ Attendre le rechargement
    } catch (error) {
      // Extract error message from API response
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(errorMessage);
    }
  };

  const handleUpdateStatus = async (ticketId, status) => {
    try {
      await ticketService.updateTicket(ticketId, { statut: status });
      toast.success('Statut mis à jour');
      await fetchData();  // ✅ Attendre le rechargement des données
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleEdit = (ticket) => {
    setEditingTicket(ticket);
    setShowFormModal(true);
  };

  // Handler for manual attribution from VENTE view
  const handleAttribuerVente = (venteTicket) => {
    setSelectedVenteForAttribution(venteTicket);
    setShowAttribuerVenteModal(true);
  };

  // Handler for special attribution (different category)
  const handleSpecialAttribution = (venteTicket) => {
    setSelectedVenteForAttribution(venteTicket);
    setShowSpecialAttributionModal(true);
  };

  const handleMarkAttribue = async (ticketId, attribue) => {
    try {
      // Get the ticket BEFORE the API call to get its ref_vente
      const targetTicket = tickets.find(t => t.id === ticketId);
      const targetRefVente = targetTicket?.ref_vente || '';
      
      const result = await ticketService.markAttribue(ticketId, attribue);
      
      if (!attribue) {
        // Désattribution - update locally based on ref_vente
        // Use either the result ref_vente or the ticket's own ref_vente
        const refVente = result.ref_vente || targetRefVente;
        toast.success(result.message || 'Désattribué');
        
        // Update tickets locally - for ACHAT clear data, for VENTE only clear attribue/statut
        setTickets(prev => prev.map(t => {
          // Check if this ticket is affected:
          // 1. Same id as clicked ticket
          // 2. Has same ref_vente as the target (for cascade)
          const isClickedTicket = t.id === ticketId;
          const hasSameRefVente = refVente && refVente.trim() !== '' && t.ref_vente === refVente;
          const isAffected = isClickedTicket || hasSameRefVente;
          
          if (isAffected) {
            if (t.type === 'ACHAT') {
              // ACHAT: clear all attribution data
              return { 
                ...t, 
                attribue: false, 
                ref_vente: '', 
                ref_vente_attribuer: '',
                nom_client: '', 
                mail_client: '',
                statut: ''
              };
            } else {
              // VENTE: only clear attribue and statut, KEEP ref_vente, nom_client, mail_client
              return { 
                ...t, 
                attribue: false, 
                statut: ''
              };
            }
          }
          return t;
        }));
        
        // Rafraîchir les stats et delta
        const [newStats, newDelta] = await Promise.all([
          ticketService.getStats(currentProject),
          ticketService.getDelta(currentProject)
        ]);
        setStats(newStats);
        setDelta(newDelta);
      } else {
        // Attribuer - update locally
        setTickets(prev => prev.map(t => {
          if (t.id === ticketId) {
            return { ...t, attribue: true };
          }
          return t;
        }));
        toast.success('Attribué');
        
        // Rafraîchir les stats et delta
        const [newStats, newDelta] = await Promise.all([
          ticketService.getStats(currentProject),
          ticketService.getDelta(currentProject)
        ]);
        setStats(newStats);
        setDelta(newDelta);
      }
    } catch (error) {
      console.error('Mark attribue error:', error);
      toast.error(`Erreur: ${error.response?.data?.detail || error.message || 'Erreur de connexion'}`);
    }
  };

  // Handler to validate a ticket (mark as VALIDE - green)
  const handleValidateTicket = async (ticketId) => {
    try {
      const result = await ticketService.validateTicket(ticketId);
      const refVente = result.ref_vente;
      
      toast.success(result.message);
      
      // Update tickets locally - set statut to VALIDE for all related tickets
      setTickets(prev => prev.map(t => {
        // Check if this ticket is affected (same id or same ref_vente)
        const isAffected = t.id === ticketId || (refVente && t.ref_vente === refVente);
        
        if (isAffected) {
          return { 
            ...t, 
            statut: 'VALIDE',
            attribue: true
          };
        }
        return t;
      }));
      
      // Rafraîchir les stats et delta
      const [newStats, newDelta] = await Promise.all([
        ticketService.getStats(currentProject),
        ticketService.getDelta(currentProject)
      ]);
      setStats(newStats);
      setDelta(newDelta);
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Erreur lors de la validation';
      
      if (detail) {
        errorMsg = detail;
      } else if (error.message?.includes('Network')) {
        errorMsg = 'Erreur de connexion au serveur. Vérifiez votre connexion internet.';
      } else if (error.message?.includes('timeout')) {
        errorMsg = 'Le serveur met trop de temps à répondre. Réessayez.';
      }
      
      toast.error(errorMsg);
    }
  };

  const handleRunMatching = async (silent = false) => {
    if (matcherRunning) return;
    setMatcherRunning(true);
    try {
      if (!silent) toast.info('Attribution automatique en cours...');
      const result = await ticketService.autoMatch(currentProject);
      setLastMatcherRun(new Date());
      if (!silent || result.matched_ventes > 0) {
        toast.success(result.message || `${result.matched_ventes} match(s) trouvé(s)`);
      }
      fetchData();
    } catch (error) {
      console.error('Matching error:', error);
      if (!silent) {
        let errorMsg = 'Erreur inconnue';
        const detail = error.response?.data?.detail;
        
        if (detail) {
          errorMsg = detail;
        } else if (error.message?.includes('timeout') || error.code === 'ECONNABORTED') {
          errorMsg = 'Le matching prend trop de temps. Avec beaucoup de tickets, cela peut durer plusieurs minutes. Réessayez ou attendez.';
        } else if (error.message?.includes('Network') || error.code === 'ERR_NETWORK') {
          errorMsg = 'Erreur de connexion au serveur. Vérifiez votre connexion internet.';
        } else if (error.response?.status === 500) {
          errorMsg = 'Erreur serveur lors du matching. Contactez le support si le problème persiste.';
        } else {
          errorMsg = error.message || 'Erreur inconnue';
        }
        
        toast.error(`Erreur matching: ${errorMsg}`);
      }
    } finally {
      setMatcherRunning(false);
    }
  };

  // Ouvrir la modal de sélection des sources à ignorer
  const handleOpenMatcherFilterModal = () => {
    setShowMatcherFilterModal(true);
  };

  // Matching filtré avec les sources sélectionnées
  const handleRunMatchingFiltered = async (excludedSources) => {
    if (matcherRunning) return;
    setMatcherRunning(true);
    setShowMatcherFilterModal(false);
    
    try {
      const excludedStr = excludedSources.length > 0 
        ? `sans ${excludedSources.join(', ')}` 
        : 'toutes sources';
      toast.info(`Attribution en cours (${excludedStr})...`);
      
      const result = await ticketService.autoMatchFiltered(currentProject, excludedSources);
      setLastMatcherRun(new Date());
      
      const matchedCount = result.matched_ventes || 0;
      if (matchedCount > 0) {
        toast.success(`${matchedCount} match(s) trouvé(s) !`);
      } else {
        toast.info('Aucun nouveau match trouvé.');
      }
      
      fetchData();
    } catch (error) {
      console.error('Matching filtered error:', error);
      let errorMsg = 'Erreur inconnue';
      const detail = error.response?.data?.detail;
      
      if (detail) {
        errorMsg = detail;
      } else if (error.message?.includes('timeout') || error.code === 'ECONNABORTED') {
        errorMsg = 'Le matching prend trop de temps. Réessayez.';
      } else if (error.response?.status === 500) {
        errorMsg = 'Erreur serveur lors du matching.';
      } else {
        errorMsg = error.message || 'Erreur inconnue';
      }
      
      toast.error(`Erreur matching: ${errorMsg}`);
    } finally {
      setMatcherRunning(false);
    }
  };

  // Auto Matcher Timer
  const autoMatcherRef = useRef(null);
  
  useEffect(() => {
    if (autoMatcherEnabled && isAuthenticated) {
      // Clear any existing interval
      if (autoMatcherRef.current) {
        clearInterval(autoMatcherRef.current);
      }
      
      // Run immediately on enable
      handleRunMatching(true);
      
      // Set up interval
      autoMatcherRef.current = setInterval(() => {
        handleRunMatching(true);
      }, autoMatcherInterval * 1000);
      
      toast.success(`Auto-Matcher activé (${autoMatcherInterval}s)`);
    } else {
      if (autoMatcherRef.current) {
        clearInterval(autoMatcherRef.current);
        autoMatcherRef.current = null;
      }
    }
    
    return () => {
      if (autoMatcherRef.current) {
        clearInterval(autoMatcherRef.current);
      }
    };
  }, [autoMatcherEnabled, autoMatcherInterval, isAuthenticated, currentProject]);

  const toggleAutoMatcher = () => {
    setAutoMatcherEnabled(prev => !prev);
  };

  // Auto Email Sync - handler function
  const handleAutoSync = async (silent = false) => {
    if (syncRunning) return;
    setSyncRunning(true);
    try {
      const result = await ticketService.autoSyncEmails(currentProject);
      setLastSyncRun(new Date());
      
      const hasNewData = result.new_tickets > 0 || result.updated_tickets > 0;
      
      if (hasNewData) {
        setSyncStats({
          newTickets: syncStats.newTickets + result.new_tickets,
          updatedTickets: syncStats.updatedTickets + result.updated_tickets
        });
        
        // Refresh data when new tickets found
        fetchData();
        
        if (!silent) {
          toast.success(`📧 ${result.new_tickets} nouveau(x), ${result.updated_tickets} mis à jour`);
        } else {
          // Show subtle notification even in silent mode when new data
          toast.info(`📧 Sync: ${result.message}`, { duration: 3000 });
        }
      }
    } catch (error) {
      if (!silent) {
        let errorMsg = 'Erreur sync email';
        const detail = error.response?.data?.detail;
        
        if (detail) {
          errorMsg = detail;
        } else if (error.message?.includes('timeout')) {
          errorMsg = 'La synchronisation email a pris trop de temps. Réessayez.';
        } else if (error.response?.status === 401) {
          errorMsg = 'Identifiants email incorrects. Vérifiez la configuration.';
        } else if (error.response?.status === 500) {
          errorMsg = 'Erreur serveur lors de la synchronisation. Le serveur IMAP est peut-être inaccessible.';
        }
        
        toast.error(errorMsg);
      }
      console.error('Auto-sync error:', error);
    } finally {
      setSyncRunning(false);
    }
  };

  // 🔄 RE-SCAN COMPLET - Vérifie tous les emails même ceux déjà traités
  const [forceRescanRunning, setForceRescanRunning] = useState(false);
  
  const handleForceRescan = async () => {
    if (forceRescanRunning) return;
    
    setForceRescanRunning(true);
    toast.info('🔄 Re-scan complet en cours... Cela peut prendre plusieurs minutes.');
    
    try {
      const result = await ticketService.forceRescanEmails(currentProject);
      
      if (result.new_tickets > 0 || result.updated_tickets > 0) {
        fetchData();
        toast.success(`✅ Re-scan terminé: ${result.new_tickets} nouveau(x), ${result.updated_tickets} mis à jour`);
      } else {
        toast.success('✅ Re-scan terminé: Tous les tickets étaient déjà importés');
      }
    } catch (error) {
      toast.error('❌ Erreur lors du re-scan complet');
      console.error('Force rescan error:', error);
    } finally {
      setForceRescanRunning(false);
    }
  };

  // Auto Email Sync Timer
  const autoSyncRef = useRef(null);
  
  useEffect(() => {
    if (autoSyncEnabled && isAuthenticated) {
      // Clear any existing interval
      if (autoSyncRef.current) {
        clearInterval(autoSyncRef.current);
      }
      
      // Run immediately on enable
      handleAutoSync(true);
      
      // Set up interval
      autoSyncRef.current = setInterval(() => {
        handleAutoSync(true);
      }, autoSyncInterval * 1000);
      
      toast.success(`📧 Sync auto activée (${autoSyncInterval}s)`);
    } else {
      if (autoSyncRef.current) {
        clearInterval(autoSyncRef.current);
        autoSyncRef.current = null;
      }
    }
    
    return () => {
      if (autoSyncRef.current) {
        clearInterval(autoSyncRef.current);
      }
    };
  }, [autoSyncEnabled, autoSyncInterval, isAuthenticated, currentProject]);

  const toggleAutoSync = () => {
    setAutoSyncEnabled(prev => !prev);
  };

  // ==================== SERVER-SIDE BACKGROUND SYNC (24/7) ====================
  
  // Fetch server sync status on mount and periodically
  const fetchServerSyncStatus = useCallback(async () => {
    try {
      const status = await ticketService.getBackgroundSyncStatus();
      setServerSyncStatus(status);
    } catch (error) {
      console.error('Error fetching server sync status:', error);
    }
  }, []);

  // Check server sync status on mount and every 30 seconds
  useEffect(() => {
    if (isAuthenticated) {
      fetchServerSyncStatus();
      const interval = setInterval(fetchServerSyncStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchServerSyncStatus]);

  // Start server-side background sync
  const handleStartServerSync = async (intervalMinutes = 5) => {
    setServerSyncLoading(true);
    try {
      const result = await ticketService.startBackgroundSync(intervalMinutes, currentProject);
      setServerSyncStatus(result.status);
      toast.success(result.message);
    } catch (error) {
      toast.error('Erreur lors du démarrage du sync serveur');
    } finally {
      setServerSyncLoading(false);
    }
  };

  // Stop server-side background sync
  const handleStopServerSync = async () => {
    setServerSyncLoading(true);
    try {
      const result = await ticketService.stopBackgroundSync();
      setServerSyncStatus(result.status);
      toast.success(result.message);
    } catch (error) {
      toast.error('Erreur lors de l\'arrêt du sync serveur');
    } finally {
      setServerSyncLoading(false);
    }
  };

  // State for confirmation dialog
  const [showDesattribuerConfirm, setShowDesattribuerConfirm] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);

  const handleDesattribuerAll = async () => {
    // Show custom confirmation instead of window.confirm
    setShowDesattribuerConfirm(true);
  };

  const confirmDesattribuerAll = async () => {
    setShowDesattribuerConfirm(false);
    try {
      const result = await ticketService.desattribuerAllWithBackup(currentProject, userName || 'SYSTÈME');
      toast.success(result.message || 'Tout désattribué avec succès');
      
      // Update ALL tickets locally instead of refetching (prevents flickering)
      setTickets(prev => prev.map(t => {
        if (t.type === 'ACHAT') {
          // ACHAT: clear all attribution data
          return {
            ...t,
            attribue: false,
            ref_vente: '',
            ref_vente_attribuer: '',
            nom_client: '',
            mail_client: '',
            statut: ''
          };
        } else {
          // VENTE: only clear attribue and statut, keep ref_vente/client data
          return {
            ...t,
            attribue: false,
            statut: ''
          };
        }
      }));
      
      // Refresh stats only (lightweight)
      const newStats = await ticketService.getStats(currentProject);
      setStats(newStats);
    } catch (error) {
      console.error('Erreur désattribution:', error);
      const errorMsg = error?.response?.data?.detail || error?.message || 'Erreur inconnue';
      toast.error(`Erreur: ${errorMsg}`);
    }
  };

  const loadSnapshots = async () => {
    setLoadingSnapshots(true);
    try {
      const data = await ticketService.getSnapshots(currentProject);
      setSnapshots(data || []);
    } catch (e) {
      console.error(e);
    }
    setLoadingSnapshots(false);
  };

  const handleRestoreSnapshot = async (snapshotId) => {
    if (!window.confirm('Restaurer cette version ? Toutes les données actuelles seront remplacées.')) return;
    try {
      const result = await ticketService.restoreSnapshot(snapshotId, currentProject);
      if (result.success) {
        toast.success(`Version restaurée (${result.restored_count} tickets)`);
        setShowHistoryModal(false);
        await fetchData();  // ✅ Attendre le rechargement
      } else {
        toast.error(result.error || 'Erreur');
      }
    } catch (e) {
      toast.error('Erreur lors de la restauration');
    }
  };

  const handleClearLogs = async () => {
    try {
      await ticketService.clearLogs();
      toast.success('Logs effacés');
      setLogs([]);
    } catch (error) {
      toast.error('Erreur lors de l\'effacement');
    }
  };

  // Mark rocketposter tickets
  const handleMarkRocketposter = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tickets/mark-rocketposter?project=${encodeURIComponent(currentProject)}`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error('Erreur serveur');
      }
      
      const result = await response.json();
      
      if (result.updated > 0) {
        toast.success(`✅ ${result.updated} tickets marqués "rocketposter"`, {
          description: `${result.already_marked} déjà marqués`
        });
        // Refresh data to see the changes
        await fetchData();
      } else if (result.already_marked > 0) {
        toast.info(`Tous les ${result.already_marked} tickets rocketposter sont déjà marqués`);
      } else {
        toast.info('Aucun ticket avec 0@rocketposter.com trouvé');
      }
    } catch (error) {
      console.error('Error marking rocketposter:', error);
      toast.error('Erreur lors du marquage rocketposter');
    }
  };

  // Export data to CSV - simple and clean
  const handleExportCSV = (exportAll = true) => {
    const timestamp = new Date().toISOString().slice(0, 10);
    
    // CSV helper functions
    const escape = (val) => `"${String(val || '').replace(/"/g, '""')}"`;
    const toCSV = (headers, rows) => {
      const headerLine = headers.map(escape).join(';');
      const dataLines = rows.map(row => row.map(escape).join(';'));
      return [headerLine, ...dataLines].join('\n');
    };
    const downloadCSV = (content, filename) => {
      const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    };

    if (exportAll) {
      // Export ALL data - 3 files
      // ACHAT: MATCH | CATEGORIE | E-MAIL DU COMPTE | PW DU COMPTE | SOURCE | REF DE VENTE | NOM CLIENT | MAIL CLIENT | PREUVE ENVOI
      const achatHeaders = ['MATCH', 'CATÉGORIE', 'E-MAIL DU COMPTE', 'PW DU COMPTE', 'SOURCE', 'REF DE VENTE', 'NOM CLIENT', 'MAIL CLIENT', 'PREUVE ENVOI', 'LIEU', 'VILLE', 'PAYS', 'DATE', 'QTY', 'ATTRIBUÉ', 'STATUT'];
      const achatRows = achatTickets.map(t => [
        t.evenement, t.categorie, t.email_compte, t.pw_compte || '', t.source,
        t.ref_vente || '', t.nom_client || '', t.mail_client || '', t.preuve_envoi || '',
        t.lieu || '', t.ville || '', t.pays || '', t.date_evenement || '', t.qty || 1,
        t.attribue ? 'Oui' : 'Non', t.statut || ''
      ]);
      
      // VENTE: REF DE VENTE | VENDU LE | PLATEFORME | MATCH | LIEU | VILLE | PAYS | DATE | CATEGORIE | QTY | PRIX | NOTE | NOM CLIENT | MAIL CLIENT | PREUVE
      const venteHeaders = ['REF DE VENTE', 'VENDU LE', 'PLATEFORME', 'MATCH', 'LIEU', 'VILLE', 'PAYS', 'DATE', 'CATÉGORIE', 'QTY', 'PRIX', 'NOTE', 'NOM CLIENT', 'MAIL CLIENT', 'PREUVE', 'ATTRIBUÉ', 'STATUT'];
      const venteRows = venteTickets.map(t => [
        t.ref_vente, t.date_operation || '', t.source, t.evenement,
        t.lieu || '', t.ville || '', t.pays || '', t.date_evenement || '',
        t.categorie, t.qty || 1, t.prix || 0, t.note || '', t.nom_client || '', t.mail_client || '',
        t.preuve_url ? `${process.env.REACT_APP_BACKEND_URL}${t.preuve_url}` : '',
        t.attribue ? 'Oui' : 'Non', t.statut || ''
      ]);
      
      const deltaHeaders = ['Événement', 'Date', 'Ville', 'Stocks'];
      const deltaRows = delta.map(d => {
        const stockStr = d.stocks ? Object.entries(d.stocks).map(([cat, val]) => `${cat}:${val}`).join(' | ') : '';
        return [d.evenement, d.date_evenement || '', d.ville || '', stockStr];
      });
      
      downloadCSV(toCSV(achatHeaders, achatRows), `YAS_ACHAT_${currentProject}_${timestamp}.csv`);
      setTimeout(() => downloadCSV(toCSV(venteHeaders, venteRows), `YAS_VENTE_${currentProject}_${timestamp}.csv`), 300);
      setTimeout(() => downloadCSV(toCSV(deltaHeaders, deltaRows), `YAS_DELTA_${currentProject}_${timestamp}.csv`), 600);
      
      toast.success(`3 fichiers exportés: ACHAT (${achatTickets.length}), VENTE (${venteTickets.length}), DELTA`);
    } else {
      // Export FILTERED data - based on active tab
      // Uses tableFilteredTickets which contains the actually filtered tickets from TicketTable
      let headers, rows, filename, count;
      
      // Use the filtered tickets from the table (includes column filters, status filters, etc.)
      const ticketsToExport = tableFilteredTickets.length > 0 ? tableFilteredTickets : 
        (activeTab === 'achat' ? filteredAchatTickets : 
         activeTab === 'vente' ? filteredVenteTickets : filteredAllTickets);
      
      // Add search filter to filename if present
      const filterSuffix = '_FILTRE';
      
      if (activeTab === 'achat') {
        headers = ['MATCH', 'CATÉGORIE', 'E-MAIL DU COMPTE', 'PW DU COMPTE', 'SOURCE', 'REF DE VENTE', 'NOM CLIENT', 'MAIL CLIENT', 'PREUVE ENVOI', 'LIEU', 'VILLE', 'PAYS', 'DATE', 'QTY', 'ATTRIBUÉ', 'STATUT'];
        const achatToExport = ticketsToExport.filter(t => t.type === 'ACHAT');
        rows = achatToExport.map(t => [
          t.evenement, t.categorie, t.email_compte, t.pw_compte || '', t.source,
          t.ref_vente || '', t.nom_client || '', t.mail_client || '', t.preuve_envoi || '',
          t.lieu || '', t.ville || '', t.pays || '', t.date_evenement || '', t.qty || 1,
          t.attribue ? 'Oui' : 'Non', t.statut || ''
        ]);
        filename = `YAS_ACHAT${filterSuffix}_${currentProject}_${timestamp}.csv`;
        count = achatToExport.length;
      } else if (activeTab === 'vente') {
        headers = ['REF DE VENTE', 'VENDU LE', 'PLATEFORME', 'MATCH', 'LIEU', 'VILLE', 'PAYS', 'DATE', 'CATÉGORIE', 'QTY', 'PRIX', 'NOTE', 'NOM CLIENT', 'MAIL CLIENT', 'MAIL COMPTE (ACHAT)', 'PW COMPTE (ACHAT)', 'SOURCE ACHAT', 'PREUVE', 'ATTRIBUÉ', 'STATUT'];
        const venteToExport = ticketsToExport.filter(t => t.type === 'VENTE');
        rows = venteToExport.map(t => [
          t.ref_vente, t.date_operation || '', t.source, t.evenement,
          t.lieu || '', t.ville || '', t.pays || '', t.date_evenement || '',
          t.categorie, t.qty || 1, t.prix || 0, t.note || '', t.nom_client || '', t.mail_client || '',
          t.linked_mail_du_compte || '', t.linked_pw_du_compte || '', t.linked_source_achat || '',
          t.preuve_url ? `${process.env.REACT_APP_BACKEND_URL}${t.preuve_url}` : '',
          t.attribue ? 'Oui' : 'Non', t.statut || ''
        ]);
        filename = `YAS_VENTE${filterSuffix}_${currentProject}_${timestamp}.csv`;
        count = venteToExport.length;
      } else if (activeTab === 'tout') {
        headers = ['TYPE', 'MATCH', 'CATÉGORIE', 'DATE', 'QTY', 'PRIX', 'SOURCE', 'REF DE VENTE', 'VENDU LE', 'LIEU', 'VILLE', 'PAYS', 'NOTE', 'E-MAIL COMPTE', 'PW COMPTE', 'PREUVE ENVOI', 'PREUVE', 'NOM CLIENT', 'MAIL CLIENT', 'ATTRIBUÉ', 'STATUT'];
        rows = ticketsToExport.map(t => [
          t.type, t.evenement, t.categorie, t.date_evenement || '', t.qty || 1, t.prix || 0, t.source,
          t.ref_vente || '', t.date_operation || '', t.lieu || '', t.ville || '', t.pays || '', t.note || '',
          t.email_compte || '', t.pw_compte || '', t.preuve_envoi || '',
          t.preuve_url ? `${process.env.REACT_APP_BACKEND_URL}${t.preuve_url}` : '',
          t.nom_client || '', t.mail_client || '',
          t.attribue ? 'Oui' : 'Non', t.statut || ''
        ]);
        filename = `YAS_TOUT${filterSuffix}_${currentProject}_${timestamp}.csv`;
        count = ticketsToExport.length;
      } else {
        toast.error('Sélectionnez un onglet ACHAT, VENTE ou TOUT pour exporter');
        return;
      }
      
      downloadCSV(toCSV(headers, rows), filename);
      toast.success(`${count} lignes exportées (filtrées): ${filename}`);
    }
  };

  // Handle click on DELTA category - navigate to filtered view
  const handleDeltaCategoryClick = ({ viewType, eventName, dateEvenement, matchNum, category, stock }) => {
    if (!/WORLD CUP|ROLAND/.test((currentProject || '').toUpperCase())) matchNum = null;
    // Set delta filter and switch to the appropriate tab
    setDeltaFilter({ matchNum, category, eventName, dateEvenement, stock });
    // Filter by NON-ATTRIBUTED tickets only (Delta shows available stock)
    setStatusFilter('non_attribue');
    setActiveTab(viewType); // 'achat' if +, 'vente' if -
    toast.info(`Filtre: ${matchNum != null ? `M${matchNum}` : eventName} - ${category} - Non attribués (${stock > 0 ? '+' : ''}${stock})`);
  };

  // Clear delta filter and return to DELTA view
  const clearDeltaFilter = () => {
    setDeltaFilter(null);
    setActiveTab('delta');
  };

  // Helper to extract match number from event name
  const extractMatchNum = (eventName) => {
    if (!eventName) return null;
    const match = eventName.match(/\b[M]?0*(\d{1,3})\b/i);
    return match ? parseInt(match[1], 10) : null;
  };

  // Filter tickets based on status filter, delta filter, AND global search term
  const getFilteredTickets = (baseTickets) => {
    let result = baseTickets;
    
    // Apply status filter
    if (statusFilter) {
      if (statusFilter === 'attribue') {
        result = result.filter(t => t.attribue === true);
      } else if (statusFilter === 'non_attribue') {
        // Non attributed = NOT attribue AND NOT validated
        result = result.filter(t => t.attribue !== true && t.statut !== 'VALIDE');
      } else {
        result = result.filter(t => t.statut === statusFilter);
      }
    }
    
    // Apply delta filter (match number + category)
    if (deltaFilter) {
      // Function to normalize category for comparison
      // IMPORTANT: Keep special categories as-is, only normalize simple "CAT X" variants
      const normalizeCategory = (cat) => {
        if (!cat) return 'SANS CATEGORIE';
        const catUpper = cat.toUpperCase().trim();
        
        // KEEP special categories as-is (don't normalize them)
        const specialKeywords = ['EASY', 'ACCESS', 'WHEELCHAIR', 'SUPORTER', 'SUPPORTER', 
                                 'STANDAR', 'AMENITY', 'LEVEL', 'SIDELINE', 'SINGLES',
                                 'PITCH', 'BEST AVAILABLE', 'LOUNGE', 'HOSPITALITY',
                                 'UPPER', 'LOWER', '100-LEVEL', '200-LEVEL', '300-LEVEL', '400-LEVEL'];
        
        for (const keyword of specialKeywords) {
          if (catUpper.includes(keyword)) {
            return catUpper; // Return as-is, don't normalize
          }
        }
        
        // Handle placeholders
        if (['XXX', 'X', 'NA', 'N/A', '-'].includes(catUpper)) {
          return catUpper;
        }
        
        // Only normalize SIMPLE category names (CAT 1, CATEGORY 2, etc.)
        const simpleCatMatch = catUpper.match(/^(CAT|CATEGORY|CATEGORIE|CATÉGORIE)\s*(\d+)$/);
        if (simpleCatMatch) {
          return `CATEGORIE ${simpleCatMatch[2]}`;
        }
        
        // Handle "CAT1", "CAT2" without space
        const simpleCatMatch2 = catUpper.match(/^(CAT|CATEGORY|CATEGORIE|CATÉGORIE)(\d+)$/);
        if (simpleCatMatch2) {
          return `CATEGORIE ${simpleCatMatch2[2]}`;
        }
        
        // Handle VIP standalone
        if (catUpper === 'VIP') return 'VIP';
        
        // Handle numbered categories like "1", "2", "3" alone
        if (/^\d+$/.test(catUpper) && parseInt(catUpper) <= 10) {
          return `CATEGORIE ${catUpper}`;
        }
        
        return catUpper;
      };

      result = result.filter(t => {
        // Concerts / événements sans numéro de match: filtre par nom + date
        const matchesEvent = deltaFilter.matchNum == null && deltaFilter.eventName
          ? (t.evenement || '').trim().toUpperCase() === deltaFilter.eventName.trim().toUpperCase() &&
            (!deltaFilter.dateEvenement || (t.date_evenement || '') === deltaFilter.dateEvenement)
          : extractMatchNum(t.evenement) === deltaFilter.matchNum;
        
        // Normalize both categories for comparison
        const normalizedTicketCat = normalizeCategory(t.categorie);
        const normalizedFilterCat = normalizeCategory(deltaFilter.category);
        
        const matchesCategory = !deltaFilter.category || 
          deltaFilter.category === 'SANS CATEGORIE' || 
          normalizedTicketCat === normalizedFilterCat;
        return matchesEvent && matchesCategory;
      });
    }
    
    // Apply global search term (for export and filtering across tabs)
    // Utilise normalizeAccents pour une recherche insensible aux accents
    if (globalSearchTerm) {
      const term = normalizeAccents(globalSearchTerm);
      result = result.filter(t =>
        normalizeAccents(t.evenement).includes(term) ||
        normalizeAccents(t.ref_achat).includes(term) ||
        normalizeAccents(t.ref_vente).includes(term) ||
        normalizeAccents(t.categorie).includes(term) ||
        normalizeAccents(t.source).includes(term) ||
        normalizeAccents(t.nom_client).includes(term) ||
        normalizeAccents(t.mail_client).includes(term) ||
        normalizeAccents(t.email_compte).includes(term) ||
        normalizeAccents(t.linked_mail_du_compte).includes(term) ||
        normalizeAccents(t.lieu).includes(term) ||
        normalizeAccents(t.ville).includes(term) ||
        normalizeAccents(t.pays).includes(term) ||
        normalizeAccents(t.note).includes(term) ||
        normalizeAccents(t.statut).includes(term)
      );
    }
    
    return result;
  };

  const achatTickets = tickets.filter(t => t.type === 'ACHAT');
  const venteTickets = tickets.filter(t => t.type === 'VENTE');
  
  const filteredAllTickets = getFilteredTickets(tickets);
  const filteredAchatTickets = getFilteredTickets(achatTickets);
  const filteredVenteTickets = getFilteredTickets(venteTickets);

  // Show loading while checking auth
  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0d0b09' }}>
        <div className="text-[#D4AF37] text-xl">Chargement...</div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <Toaster richColors position="top-right" />
        <LoginPage onLogin={handleLogin} />
      </>
    );
  }

  // ÉCRAN DE TRANSITION - Changement de projet en cours
  if (projectSwitching || (loading && tickets.length === 0 && !stats)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ backgroundColor: '#0d0b09' }}>
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#D4AF37] border-t-transparent mx-auto"></div>
          <div className="text-[#D4AF37] text-2xl font-bold">{currentProject}</div>
          <div className="text-zinc-400 text-sm">Chargement des données...</div>
        </div>
      </div>
    );
  }

  // Déterminer si on est sur Roland Garros pour le thème
  const isRolandGarros = currentProject?.toUpperCase().includes('ROLAND') || currentProject?.toUpperCase().includes('RG');
  const isWorldCup = currentProject?.toUpperCase().includes('WORLD CUP') || currentProject?.toUpperCase().includes('WC 2026');

  // URLs des assets par projet
  const projectThemes = {
    rolandGarros: {
      logo: 'https://customer-assets.emergentagent.com/job_02239018-f5c6-41eb-92ff-3437cce99ffb/artifacts/02ntrxea_image.png',
      background: 'https://images.pexels.com/photos/18213629/pexels-photo-18213629.jpeg?auto=compress&cs=tinysrgb&w=1920',
      titleColor: '#1B5E20',
    },
    worldCup: {
      logo: 'https://customer-assets.emergentagent.com/job_02239018-f5c6-41eb-92ff-3437cce99ffb/artifacts/0hu3mfvi_image.png',
      background: 'https://customer-assets.emergentagent.com/job_02239018-f5c6-41eb-92ff-3437cce99ffb/artifacts/hdjwo1rd_image.png',
      titleColor: '#D4AF37',
    }
  };

  const hasCustomTheme = isRolandGarros || isWorldCup;
  const currentTheme = isRolandGarros ? projectThemes.rolandGarros : isWorldCup ? projectThemes.worldCup : null;

  return (
    <div className={`min-h-screen relative ${isRolandGarros ? 'rg-theme' : ''} ${isWorldCup ? 'wc-theme' : ''}`} style={{ backgroundColor: isDarkMode ? '#0d0b09' : '#f5f5f5' }}>
      {/* Fond thématique selon le projet */}
      {hasCustomTheme && currentTheme && (
        <div 
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            backgroundImage: `url('${currentTheme.background}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: isDarkMode ? 0.35 : 0.25,
          }}
        />
      )}
      
      {/* Overlay gradient pour lisibilité */}
      {hasCustomTheme && (
        <div 
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            background: isDarkMode 
              ? 'linear-gradient(180deg, rgba(13, 11, 9, 0.7) 0%, rgba(13, 11, 9, 0.3) 40%, rgba(13, 11, 9, 0.5) 100%)'
              : 'linear-gradient(180deg, rgba(255, 255, 255, 0.75) 0%, rgba(255, 255, 255, 0.4) 40%, rgba(255, 255, 255, 0.6) 100%)',
          }}
        />
      )}
      
      <div className="relative z-10">
        <Toaster richColors position="top-right" />
        
        {/* Header */}
        <header className="glass-header sticky top-0 z-40 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 relative">
            {/* Logo selon le projet (Roland Garros, World Cup, ou YAS par défaut) */}
            {hasCustomTheme && currentTheme ? (
              <div 
                onClick={() => setShowProjectMenu(!showProjectMenu)}
                className="cursor-pointer flex items-center gap-3"
              >
                <img 
                  src={currentTheme.logo} 
                  alt={currentProject}
                  className="h-16 w-auto object-contain"
                  style={{ maxHeight: '64px' }}
                />
              </div>
            ) : (
              <div 
                onClick={() => setShowProjectMenu(!showProjectMenu)}
                className="cursor-pointer"
              >
                <YASLogo />
              </div>
            )}
            <div className="flex flex-col">
              <span 
                className="text-xl font-bold tracking-wide"
                style={{ color: currentTheme?.titleColor || '#D4AF37' }}
              >
                {currentProject}
              </span>
              <span className="text-zinc-500 text-xs">Cliquez sur le logo pour changer</span>
            </div>
            
            {/* Project Menu */}
            {showProjectMenu && (
              <div className="absolute top-full left-0 mt-2 bg-zinc-900 border border-[#D4AF37]/30 rounded-lg shadow-xl z-50 min-w-[250px]">
                <div className="p-2">
                  <p className="text-xs text-zinc-400 px-2 pb-2">Choisir un projet :</p>
                  {projectsLoading ? (
                    <div className="text-center py-4 text-zinc-500">Chargement...</div>
                  ) : projects.length === 0 ? (
                    <div className="text-center py-4 text-zinc-500">Aucun projet</div>
                  ) : (
                    projects.map((proj) => (
                      <button
                        key={proj}
                        onClick={() => requestProjectChange(proj)}
                        className={`w-full flex items-center px-3 py-2 rounded text-sm transition-colors ${
                          currentProject === proj 
                            ? 'bg-[#D4AF37] text-black font-semibold' 
                            : 'text-white hover:bg-zinc-800'
                        }`}
                      >
                      <span className="flex-1 text-left truncate">{proj}</span>
                      {currentProject === proj && (
                        <span className="text-xs ml-2">✓ actif</span>
                      )}
                      {/* Bouton supprimer (sauf projet actif) */}
                      {currentProject !== proj && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteProject(proj);
                          }}
                          className="ml-2 p-1 rounded hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-colors"
                          title="Supprimer ce projet"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </button>
                  ))
                  )}
                  <hr className="my-2 border-zinc-700" />
                  <button
                    onClick={handleAddProject}
                    className="w-full text-left px-3 py-2 rounded text-sm text-[#D4AF37] hover:bg-zinc-800 flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Nouveau projet
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex flex-col gap-2">
            {/* === LIGNE 1: Boîte Mail, Actualiser, Matcher, Attribution, Nouveau, Importer === */}
            <div className="flex items-center gap-3">
              {/* Email Sync avec mode AUTO */}
              <div className="flex items-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowInboxModal(true)}
                  data-testid="inbox-button"
                  className={`btn-shiny rounded-r-none border-r-0 ${
                    autoSyncEnabled 
                      ? 'border-blue-500/60 bg-blue-500/20 text-blue-400 hover:bg-blue-500 hover:text-black' 
                      : 'border-[#D4AF37]/40 bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4B86A] hover:text-black'
                  } transition-all duration-300`}
                >
                  {syncRunning ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Inbox className="h-4 w-4 mr-2" />
                  )}
                  Boîte Mail
                  {autoSyncEnabled && (
                    <span className="ml-2 px-1.5 py-0.5 bg-blue-500 text-black text-xs rounded font-bold">
                      SYNC
                    </span>
                  )}
                </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="auto-sync-dropdown"
                    className={`rounded-l-none px-2 ${
                      autoSyncEnabled 
                        ? 'border-blue-500/60 bg-blue-500/20 text-blue-400 hover:bg-blue-500 hover:text-black' 
                        : 'border-[#D4AF37]/40 bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4B86A] hover:text-black'
                    } transition-all duration-300`}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 bg-zinc-900 border-zinc-700">
                  <div className="px-3 py-2 border-b border-zinc-700">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-white">Sync Auto Emails</span>
                      <Button
                        size="sm"
                        onClick={toggleAutoSync}
                        data-testid="toggle-auto-sync"
                        className={`h-7 px-3 ${
                          autoSyncEnabled 
                            ? 'bg-blue-500 hover:bg-blue-600 text-black' 
                            : 'bg-zinc-700 hover:bg-zinc-600 text-zinc-300'
                        }`}
                      >
                        {autoSyncEnabled ? (
                          <>
                            <Pause className="h-3 w-3 mr-1" />
                            STOP
                          </>
                        ) : (
                          <>
                            <Play className="h-3 w-3 mr-1" />
                            START
                          </>
                        )}
                      </Button>
                    </div>
                    {lastSyncRun && (
                      <p className="text-xs text-zinc-400 mt-1">
                        Dernier check: {lastSyncRun.toLocaleTimeString('fr-FR')}
                      </p>
                    )}
                  </div>
                  <div className="px-3 py-2">
                    <p className="text-xs text-zinc-400 mb-2">Intervalle de vérification</p>
                    <div className="grid grid-cols-4 gap-1">
                      {[30, 60, 120, 300].map((secs) => (
                        <Button
                          key={secs}
                          size="sm"
                          onClick={() => setAutoSyncInterval(secs)}
                          className={`h-7 text-xs ${
                            autoSyncInterval === secs
                              ? 'bg-blue-500 text-black'
                              : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                          }`}
                        >
                          {secs < 60 ? `${secs}s` : `${secs / 60}m`}
                        </Button>
                      ))}
                    </div>
                  </div>
                  {/* Manual sync button */}
                  <div className="px-3 py-2 border-t border-zinc-700">
                    <Button
                      size="sm"
                      onClick={() => handleAutoSync(false)}
                      disabled={syncRunning}
                      className="w-full bg-[#D4AF37] hover:bg-[#D4B86A] text-black"
                    >
                      {syncRunning ? (
                        <RefreshCw className="h-3 w-3 mr-2 animate-spin" />
                      ) : (
                        <Mail className="h-3 w-3 mr-2" />
                      )}
                      Vérifier maintenant
                    </Button>
                    
                    {/* 🔄 BOUTON RE-SCAN COMPLET */}
                    <Button
                      size="sm"
                      onClick={handleForceRescan}
                      disabled={forceRescanRunning || syncRunning}
                      className="w-full mt-2 bg-amber-600 hover:bg-amber-500 text-white"
                    >
                      {forceRescanRunning ? (
                        <RefreshCw className="h-3 w-3 mr-2 animate-spin" />
                      ) : (
                        <RotateCcw className="h-3 w-3 mr-2" />
                      )}
                      Re-scan complet
                    </Button>
                    <p className="text-xs text-zinc-500 mt-1 text-center">
                      Vérifie tous les emails (même traités)
                    </p>
                  </div>
                  {autoSyncEnabled && (
                    <div className="px-3 py-2 bg-blue-500/10 border-t border-zinc-700">
                      <div className="flex items-center gap-2 text-blue-400 text-xs">
                        <Clock className="h-3 w-3 animate-pulse" />
                        <span>Prochain check dans {autoSyncInterval}s</span>
                      </div>
                      {(syncStats.newTickets > 0 || syncStats.updatedTickets > 0) && (
                        <div className="mt-1 text-xs text-zinc-400">
                          Session: {syncStats.newTickets} nouveau(x), {syncStats.updatedTickets} màj
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* ====== SERVER-SIDE 24/7 SYNC SECTION ====== */}
                  <div className="px-3 py-3 border-t-2 border-emerald-500/50 bg-gradient-to-b from-emerald-900/20 to-transparent">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                      <span className="text-sm font-bold text-emerald-400">SYNC SERVEUR 24/7</span>
                    </div>
                    <p className="text-xs text-zinc-400 mb-3">
                      Actif même si vous fermez le navigateur
                    </p>
                    
                    {serverSyncStatus?.is_running ? (
                      <>
                        <div className="bg-emerald-500/20 border border-emerald-500/40 rounded-lg p-2 mb-2">
                          <div className="flex items-center justify-between">
                            <span className="text-emerald-400 text-xs font-medium">🟢 ACTIF</span>
                            <span className="text-emerald-300 text-xs">
                              Toutes les {serverSyncStatus.interval_display}
                            </span>
                          </div>
                          {serverSyncStatus.last_sync && (
                            <p className="text-xs text-zinc-400 mt-1">
                              Dernier sync: {new Date(serverSyncStatus.last_sync).toLocaleTimeString('fr-FR')}
                            </p>
                          )}
                          {serverSyncStatus.sync_count > 0 && (
                            <p className="text-xs text-zinc-500 mt-1">
                              Total syncs: {serverSyncStatus.sync_count}
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={handleStopServerSync}
                          disabled={serverSyncLoading}
                          className="w-full bg-red-500/80 hover:bg-red-500 text-white"
                        >
                          {serverSyncLoading ? (
                            <RefreshCw className="h-3 w-3 mr-2 animate-spin" />
                          ) : (
                            <Pause className="h-3 w-3 mr-2" />
                          )}
                          Arrêter le Sync Serveur
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-2 mb-2">
                          <span className="text-zinc-400 text-xs">🔴 INACTIF</span>
                        </div>
                        <div className="space-y-2">
                          <p className="text-xs text-zinc-500">Intervalle:</p>
                          <div className="grid grid-cols-4 gap-1 mb-2">
                            {[1, 5, 15, 30].map((mins) => (
                              <Button
                                key={mins}
                                size="sm"
                                onClick={() => handleStartServerSync(mins)}
                                disabled={serverSyncLoading}
                                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500 text-white"
                              >
                                {mins}min
                              </Button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              data-testid="refresh-button"
              className="btn-shiny border-[#D4AF37]/40 bg-transparent hover:bg-[#D4B86A] hover:text-black hover:border-[#E5C97B] text-[#D4AF37] transition-all duration-300"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
            
            {/* Matcher avec mode AUTO */}
            <div className="flex items-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRunMatching(false)}
                disabled={matcherRunning}
                data-testid="match-button"
                className={`btn-shiny rounded-r-none border-r-0 ${
                  autoMatcherEnabled 
                    ? 'border-emerald-500/60 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500 hover:text-black' 
                    : 'border-[#D4AF37]/40 bg-transparent text-[#D4AF37] hover:bg-[#D4B86A] hover:text-black'
                } transition-all duration-300`}
              >
                {matcherRunning ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Zap className="h-4 w-4 mr-2" />
                )}
                Matcher
                {autoMatcherEnabled && (
                  <span className="ml-2 px-1.5 py-0.5 bg-emerald-500 text-black text-xs rounded font-bold">
                    AUTO
                  </span>
                )}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="auto-matcher-dropdown"
                    className={`rounded-l-none px-2 ${
                      autoMatcherEnabled 
                        ? 'border-emerald-500/60 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500 hover:text-black' 
                        : 'border-[#D4AF37]/40 bg-transparent text-[#D4AF37] hover:bg-[#D4B86A] hover:text-black'
                    } transition-all duration-300`}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-700">
                  <div className="px-3 py-2 border-b border-zinc-700">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-white">Mode AUTO</span>
                      <Button
                        size="sm"
                        onClick={toggleAutoMatcher}
                        data-testid="toggle-auto-matcher"
                        className={`h-7 px-3 ${
                          autoMatcherEnabled 
                            ? 'bg-emerald-500 hover:bg-emerald-600 text-black' 
                            : 'bg-zinc-700 hover:bg-zinc-600 text-zinc-300'
                        }`}
                      >
                        {autoMatcherEnabled ? (
                          <>
                            <Pause className="h-3 w-3 mr-1" />
                            STOP
                          </>
                        ) : (
                          <>
                            <Play className="h-3 w-3 mr-1" />
                            START
                          </>
                        )}
                      </Button>
                    </div>
                    {lastMatcherRun && (
                      <p className="text-xs text-zinc-400 mt-1">
                        Dernier: {lastMatcherRun.toLocaleTimeString('fr-FR')}
                      </p>
                    )}
                  </div>
                  <div className="px-3 py-2">
                    <p className="text-xs text-zinc-400 mb-2">Intervalle</p>
                    <div className="grid grid-cols-4 gap-1">
                      {[30, 60, 120, 300].map((secs) => (
                        <Button
                          key={secs}
                          size="sm"
                          onClick={() => setAutoMatcherInterval(secs)}
                          className={`h-7 text-xs ${
                            autoMatcherInterval === secs
                              ? 'bg-[#D4AF37] text-black'
                              : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                          }`}
                        >
                          {secs < 60 ? `${secs}s` : `${secs / 60}m`}
                        </Button>
                      ))}
                    </div>
                  </div>
                  {autoMatcherEnabled && (
                    <div className="px-3 py-2 bg-emerald-500/10 border-t border-zinc-700">
                      <div className="flex items-center gap-2 text-emerald-400 text-xs">
                        <Clock className="h-3 w-3 animate-pulse" />
                        <span>Prochain dans {autoMatcherInterval}s</span>
                      </div>
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Attribution Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="attribution-dropdown-button"
                  className="btn-shiny border-[#D4AF37]/40 bg-transparent hover:bg-[#D4B86A] hover:text-black hover:border-[#E5C97B] text-[#D4AF37] transition-all duration-300"
                >
                  Attribution
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-700">
                <DropdownMenuItem
                  onClick={handleRunMatching}
                  className="text-emerald-400 focus:text-emerald-400 focus:bg-emerald-500/10"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Attribuer (Tous)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleOpenMatcherFilterModal}
                  disabled={matcherRunning}
                  className="text-amber-400 focus:text-amber-400 focus:bg-amber-500/10"
                  data-testid="match-filtered-button"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Attribuer (Choisir sources)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleDesattribuerAll}
                  className="text-red-400 focus:text-red-400 focus:bg-red-500/10"
                >
                  <X className="h-4 w-4 mr-2" />
                  Tout désattribuer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Nouveau - sur ligne 1 */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setEditingTicket(null);
                setShowFormModal(true);
              }}
              data-testid="new-ticket-button"
              className="btn-shiny border-[#D4AF37]/40 bg-transparent hover:bg-[#D4B86A] hover:text-black hover:border-[#E5C97B] text-[#D4AF37] transition-all duration-300"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouveau
            </Button>
            
            {/* Import Dropdown - sur ligne 1 */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="import-dropdown-button"
                  className="btn-shiny bg-[#D4AF37] hover:bg-[#E5C97B] text-black font-semibold transition-all duration-300"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Importer
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  data-testid="import-email-option"
                  onClick={() => setShowImportModal(true)}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Importer un Email
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="import-sheet-option"
                  onClick={() => setShowBulkImportModal(true)}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Importer depuis Sheets/Excel
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="smart-import-option"
                  onClick={() => setShowSmartImportModal(true)}
                  className="bg-[#D4AF37]/20 border-l-2 border-[#D4AF37]"
                >
                  <Zap className="h-4 w-4 mr-2 text-[#D4AF37]" />
                  <span className="text-[#D4AF37] font-medium">Import Intelligent CSV</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="google-sheets-import-option"
                  onClick={() => setShowGoogleSheetsModal(true)}
                  className="bg-green-500/10 border-l-2 border-green-500"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2 text-green-500" />
                  <span className="text-green-400 font-medium">Import Google Sheets</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>

            {/* Bouton Normaliser Matchs RG - uniquement pour Roland Garros */}
            {isRolandGarros && (
              <Button
                variant="outline"
                size="sm"
                data-testid="normalize-rg-btn"
                onClick={async () => {
                  if (!window.confirm('Normaliser les noms de matchs RG ?\nCette action corrigera automatiquement les noms mal écrits en utilisant la base de données du calendrier RG 2026.')) return;
                  try {
                    const res = await ticketService.normalizeRGMatches(currentProject);
                    toast.success(`Normalisation terminée : ${res.updated} corrigé(s), ${res.already_ok} déjà OK, ${res.no_match} non trouvé(s)`);
                    if (res.updated > 0) {
                      loadAllTickets();
                    }
                  } catch (err) {
                    toast.error('Erreur lors de la normalisation');
                  }
                }}
                className="btn-shiny border-green-500/40 bg-transparent hover:bg-green-500 hover:text-black hover:border-green-400 text-green-400 transition-all duration-300"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Normaliser Matchs
              </Button>
            )}

            {/* === LIGNE 2: Doublons, Fix Anti-Single, Vérifier Réfs === */}
            <div className="flex items-center gap-3 justify-end">
            {/* Bouton Valider en masse */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBulkValidateModal(true)}
              data-testid="bulk-validate-button"
              className="btn-shiny border-green-500/40 bg-transparent hover:bg-green-500 hover:text-black hover:border-green-400 text-green-400 transition-all duration-300"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Valider en masse
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPsgModal(true)}
              data-testid="psg-button"
              className="btn-shiny border-[#DA291C]/50 bg-[#004170]/40 hover:bg-[#004170] text-white transition-all duration-300"
            >
              ⚽ PSG
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDoublonsModal(true)}
              data-testid="check-doublons-button"
              className="btn-shiny border-[#D4AF37]/40 bg-transparent hover:bg-[#D4B86A] hover:text-black hover:border-[#E5C97B] text-[#D4AF37] transition-all duration-300"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Doublons
            </Button>
            
            {/* Bouton Corriger Anti-Single */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAntiSingleModal(true)}
              data-testid="fix-anti-single-button"
              className="btn-shiny border-orange-500/40 bg-transparent hover:bg-orange-500 hover:text-black hover:border-orange-400 text-orange-400 transition-all duration-300"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Fix Anti-Single
            </Button>
            
            {/* Bouton Vérifier Références */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRefCheckModal(true)}
              data-testid="check-refs-button"
              className="btn-shiny border-emerald-500/40 bg-transparent hover:bg-emerald-500 hover:text-black hover:border-emerald-400 text-emerald-400 transition-all duration-300"
            >
              <Search className="h-4 w-4 mr-2" />
              Vérifier Réfs
            </Button>
            
            {/* Bouton Rocketposter */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkRocketposter}
              data-testid="mark-rocketposter-button"
              className="btn-shiny border-purple-500/40 bg-transparent hover:bg-purple-500 hover:text-black hover:border-purple-400 text-purple-400 transition-all duration-300"
            >
              🚀 Rocketposter
            </Button>
            </div>
          </div>

          {/* User & Logout - séparé à droite */}
          <div className="flex items-center gap-2 ml-4 pl-4 border-l border-[#D4AF37]/30">
              {/* Theme Toggle Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="text-[#D4AF37] hover:text-[#D4B86A] hover:bg-[#D4AF37]/10"
                title={isDarkMode ? 'Mode Jour' : 'Mode Nuit'}
                data-testid="theme-toggle"
              >
                {isDarkMode ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#D4AF37] hover:text-[#D4B86A] hover:bg-[#D4AF37]/10 flex items-center gap-2"
                  >
                    <User className="h-4 w-4" />
                    <span className="font-medium">{userName}</span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-zinc-900 border-[#D4AF37]/30 w-56">
                  <DropdownMenuItem 
                    onClick={() => { loadSnapshots(); setShowHistoryModal(true); }}
                    className="cursor-pointer hover:bg-[#D4AF37]/10 text-zinc-200"
                  >
                    <History className="h-4 w-4 mr-2 text-[#D4AF37]" />
                    Historique des versions
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/debug/download-logs`, '_blank')}
                    className="cursor-pointer hover:bg-blue-500/10 text-blue-400"
                  >
                    <Bug className="h-4 w-4 mr-2" />
                    Télécharger logs debug
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="cursor-pointer hover:bg-red-500/10 text-red-400"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6 space-y-6">
        {/* Stats */}
        <StatsCards stats={stats} onTabChange={setActiveTab} onFilter={setStatusFilter} />

        {/* Active Filter Indicator - Status */}
        {statusFilter && (
          <div className="flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 rounded-lg px-4 py-2">
            <span className="text-blue-300 text-sm">
              Filtre actif: <strong>{statusFilter === 'attribue' ? 'Attribués' : statusFilter}</strong>
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setStatusFilter(null)}
              className="text-blue-300 hover:bg-blue-500/20 h-6 px-2"
            >
              ✕ Effacer
            </Button>
          </div>
        )}

        {/* Active Delta Filter Indicator */}
        {deltaFilter && (
          <div className="flex items-center gap-2 bg-[#D4AF37]/20 border border-[#D4AF37]/30 rounded-lg px-4 py-2">
            <span className="text-[#D4AF37] text-sm">
              Filtre DELTA: <strong>{deltaFilter.matchNum != null ? `M${deltaFilter.matchNum}` : `${deltaFilter.eventName || ''}${deltaFilter.dateEvenement ? ` (${deltaFilter.dateEvenement})` : ''}`} - {deltaFilter.category}</strong>
              {deltaFilter.stock != null && (
                <span className={deltaFilter.stock > 0 ? 'text-emerald-400' : 'text-red-400'}>
                  {' '}({deltaFilter.stock > 0 ? '+' : ''}{deltaFilter.stock})
                </span>
              )}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={clearDeltaFilter}
              data-testid="clear-delta-filter"
              className="text-[#D4AF37] hover:bg-[#D4AF37]/20 h-6 px-2"
            >
              ✕ Retour au DELTA
            </Button>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setStatusFilter(null); setDeltaFilter(null); setRecapDeltaFilter(null); }} className="space-y-6">
          <TabsList className="bg-[#121212] border border-[#D4AF37]/30 p-1 h-auto">
            <TabsTrigger
              value="tout"
              data-testid="tab-tout"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black px-4 py-2 font-semibold text-white"
            >
              <LayoutGrid className="h-4 w-4 mr-2" />
              TOUT
            </TabsTrigger>
            <TabsTrigger
              value="achat"
              data-testid="tab-achat"
              className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white px-4 py-2 font-semibold text-white"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              ACHAT ({stats?.total_achats || achatTickets.length})
            </TabsTrigger>
            <TabsTrigger
              value="vente"
              data-testid="tab-vente"
              className="data-[state=active]:bg-red-500 data-[state=active]:text-white px-4 py-2 font-semibold text-white"
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              VENTE ({stats?.total_ventes || venteTickets.length})
            </TabsTrigger>
            <TabsTrigger
              value="comptes"
              data-testid="tab-comptes"
              className="data-[state=active]:bg-blue-500 data-[state=active]:text-white px-4 py-2 font-semibold text-white"
            >
              <User className="h-4 w-4 mr-2" />
              COMPTES
            </TabsTrigger>
            <TabsTrigger
              value="delta"
              data-testid="tab-delta"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black px-4 py-2 font-semibold text-white"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              +/- DELTA
            </TabsTrigger>
            <TabsTrigger
              value="log"
              data-testid="tab-log"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black px-4 py-2 font-semibold text-white"
            >
              <FileText className="h-4 w-4 mr-2" />
              LOG
            </TabsTrigger>
            <TabsTrigger
              value="mapping"
              data-testid="tab-mapping"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black px-4 py-2 font-semibold text-white"
            >
              <Settings className="h-4 w-4 mr-2" />
              MAPPING STADE
            </TabsTrigger>
            <TabsTrigger
              value="backups"
              data-testid="tab-backups"
              className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white px-4 py-2 font-semibold text-white"
            >
              <Archive className="h-4 w-4 mr-2" />
              BACKUPS
            </TabsTrigger>
            <TabsTrigger
              value="market"
              data-testid="tab-market"
              className="data-[state=active]:bg-purple-600 data-[state=active]:text-white px-4 py-2 font-semibold text-white"
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              RECAP DELTA
            </TabsTrigger>
            
            {/* Onglet GROUPES - uniquement pour World Cup */}
            {isWorldCup && (
              <TabsTrigger
                value="groups"
                data-testid="tab-groups"
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white px-4 py-2 font-semibold text-white"
              >
                ⚽ GROUPES
              </TabsTrigger>
            )}
            
            {/* Onglet MATCHS RG - uniquement pour Roland Garros */}
            {isRolandGarros && (
              <TabsTrigger
                value="rg-matches"
                data-testid="tab-rg-matches"
                className="data-[state=active]:bg-green-700 data-[state=active]:text-white px-4 py-2 font-semibold text-white"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                MATCHS RG
              </TabsTrigger>
            )}
            
            {/* Export Dropdown - Gold style */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="export-dropdown-button"
                  className="ml-4 bg-[#D4AF37] hover:bg-[#D4B86A] text-black font-semibold px-4 py-2"
                >
                  <Download className="h-4 w-4 mr-2" />
                  EXPORTER
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 bg-zinc-900 border-zinc-700">
                <DropdownMenuItem
                  onClick={() => handleExportCSV(true)}
                  className="text-white focus:text-white focus:bg-[#D4AF37]/20"
                >
                  <Download className="h-4 w-4 mr-2 text-[#D4AF37]" />
                  <div className="flex flex-col">
                    <span className="font-medium">Exporter TOUT</span>
                    <span className="text-xs text-zinc-400">3 fichiers CSV (ACHAT, VENTE, DELTA)</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleExportCSV(false)}
                  className="text-white focus:text-white focus:bg-[#D4AF37]/20"
                >
                  <Filter className="h-4 w-4 mr-2 text-blue-400" />
                  <div className="flex flex-col">
                    <span className="font-medium">Exporter Onglet Actif</span>
                    <span className="text-xs text-zinc-400">Export de l'onglet {activeTab.toUpperCase()}</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TabsList>

          {/* Bouton Retour RECAP DELTA si filtre actif */}
          {recapDeltaFilter && (
            <div className="flex items-center gap-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <span className="text-purple-400">
                🔍 Filtre RECAP DELTA : <span className="font-bold text-white">{recapDeltaFilter.match}</span> - <span className="text-zinc-400">{recapDeltaFilter.category}</span>
                <span className="ml-2 text-amber-400">(non attribués)</span>
              </span>
              <Button
                size="sm"
                onClick={() => {
                  setRecapDeltaFilter(null);
                  setGlobalSearchTerm('');
                  setStatusFilter(null);
                  setDeltaFilter(null);
                  setActiveTab('market');
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                ← Retour RECAP DELTA
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setRecapDeltaFilter(null);
                  setGlobalSearchTerm('');
                  setStatusFilter(null);
                  setDeltaFilter(null);
                }}
                className="border-zinc-600 text-zinc-400 hover:text-white"
              >
                ✕ Effacer filtre
              </Button>
            </div>
          )}

          <TabsContent value="tout" className="mt-6 animate-fade-in">
            <TicketTable
              tickets={filteredAllTickets}
              onUnmatch={handleUnmatch}
              onDelete={handleDelete}
              onBulkDelete={handleBulkDelete}
              onEdit={handleEdit}
              onUpdateStatus={handleUpdateStatus}
              onMarkAttribue={handleMarkAttribue}
              onValidateTicket={handleValidateTicket}
              onAttribuerVente={handleAttribuerVente}
              onOpenProofModal={(ticket) => {
                setProofTicket(ticket);
                setShowProofModal(true);
              }}
              onOpenProofEnvoiModal={(ticket) => {
                setProofEnvoiTicket(ticket);
                setShowProofEnvoiModal(true);
              }}
              loading={loading}
              viewType="tout"
              globalSearchTerm={globalSearchTerm}
              onSearchTermChange={setGlobalSearchTerm}
              stats={stats}
              isLightMode={!isDarkMode}
              isSilentRefresh={isSilentRefresh}
              onFilteredTicketsChange={setTableFilteredTickets}
              allFilterValues={filterValues}
              project={currentProject}
            />
          </TabsContent>

          <TabsContent value="achat" className="mt-6 animate-fade-in">
            <TicketTable
              tickets={filteredAchatTickets}
              onUnmatch={handleUnmatch}
              onDelete={handleDelete}
              onBulkDelete={handleBulkDelete}
              onEdit={handleEdit}
              onUpdateStatus={handleUpdateStatus}
              onMarkAttribue={handleMarkAttribue}
              onValidateTicket={handleValidateTicket}
              onOpenProofModal={(ticket) => {
                setProofTicket(ticket);
                setShowProofModal(true);
              }}
              onOpenProofEnvoiModal={(ticket) => {
                setProofEnvoiTicket(ticket);
                setShowProofEnvoiModal(true);
              }}
              loading={loading}
              viewType="achat"
              globalSearchTerm={globalSearchTerm}
              onSearchTermChange={setGlobalSearchTerm}
              stats={stats}
              isLightMode={!isDarkMode}
              isSilentRefresh={isSilentRefresh}
              onFilteredTicketsChange={setTableFilteredTickets}
              allFilterValues={filterValues}
              project={currentProject}
            />
          </TabsContent>

          <TabsContent value="vente" className="mt-6 animate-fade-in">
            <TicketTable
              tickets={filteredVenteTickets}
              onUnmatch={handleUnmatch}
              onDelete={handleDelete}
              onBulkDelete={handleBulkDelete}
              onEdit={handleEdit}
              onUpdateStatus={handleUpdateStatus}
              onMarkAttribue={handleMarkAttribue}
              onValidateTicket={handleValidateTicket}
              onAttribuerVente={handleAttribuerVente}
              onOpenProofModal={(ticket) => {
                setProofTicket(ticket);
                setShowProofModal(true);
              }}
              loading={loading}
              viewType="vente"
              isLightMode={!isDarkMode}
              globalSearchTerm={globalSearchTerm}
              onSearchTermChange={setGlobalSearchTerm}
              stats={stats}
              isSilentRefresh={isSilentRefresh}
              onFilteredTicketsChange={setTableFilteredTickets}
              allFilterValues={filterValues}
              project={currentProject}
            />
          </TabsContent>

          <TabsContent value="delta" className="mt-6 animate-fade-in">
            <DeltaView data={delta} onCategoryClick={handleDeltaCategoryClick} />
          </TabsContent>

          <TabsContent value="comptes" className="mt-6 animate-fade-in">
            <AccountsView 
              tickets={tickets} 
              onAccountClick={(email) => {
                setSelectedAccountFilter(email);
                setGlobalSearchTerm(email); // Met le filtre de recherche global
                setActiveTab('vente'); // Aller vers VENTE pour voir les ventes liées à ce compte
              }}
              selectedAccount={selectedAccountFilter}
              onRefresh={fetchData}
            />
          </TabsContent>

          <TabsContent value="log" className="mt-6 animate-fade-in">
            <LogView logs={logs} onClear={handleClearLogs} loading={loading} />
          </TabsContent>

          <TabsContent value="mapping" className="mt-6 animate-fade-in">
            <StadiumMappingView tickets={tickets} />
          </TabsContent>

          <TabsContent value="backups" className="mt-6 animate-fade-in">
            <BackupsView currentProject={currentProject} onDataRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="market" className="mt-6 animate-fade-in">
            <MarketEvolutionView 
              currentProject={currentProject} 
              onFilterClick={(filter) => {
                // IMPORTANT: Vider la barre de recherche EN PREMIER pour éviter le double filtrage
                setGlobalSearchTerm('');
                
                // Sauvegarder le filtre pour l'affichage
                setRecapDeltaFilter(filter);
                
                // Extraire le numéro de match (M14 → 14)
                const matchNumMatch = filter.match?.match(/^M(\d+)$/i);
                const matchNum = matchNumMatch ? parseInt(matchNumMatch[1], 10) : null;
                
                // Utiliser le deltaFilter existant (match + catégorie, ou événement + date pour les concerts)
                setDeltaFilter({
                  matchNum: matchNum,
                  eventName: matchNum == null ? filter.event : undefined,
                  dateEvenement: matchNum == null ? filter.date : undefined,
                  category: filter.category || null
                });
                
                // Filtrer sur non attribués
                setStatusFilter('non_attribue');
                
                // Aller dans le bon onglet selon le type
                // ACHETER (buy) = manque de ventes → aller dans VENTE
                // VENDRE (sell) = surplus stock → aller dans ACHAT
                if (filter.type === 'buy') {
                  setActiveTab('vente');
                } else {
                  setActiveTab('achat');
                }
              }}
            />
          </TabsContent>

          {/* Onglet GROUPES World Cup */}
          {isWorldCup && (
            <TabsContent value="groups" className="mt-0">
              <WorldCupGroupsView 
                onTeamClick={(team) => {
                  // Filtrer les tickets par équipe
                  setGlobalSearchTerm(team.name);
                  setActiveTab('achat');
                }}
              />
            </TabsContent>
          )}

          {/* Onglet MATCHS RG */}
          {isRolandGarros && (
            <TabsContent value="rg-matches" className="mt-0">
              <RGMatchesView />
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Modals */}
      <EmailImportModal
        open={showImportModal}
        onOpenChange={setShowImportModal}
        onImport={handleImport}
      />

      <TicketFormModal
        open={showFormModal}
        onOpenChange={setShowFormModal}
        ticket={editingTicket}
        onSave={handleSaveTicket}
        allTickets={tickets}
      />

      <BulkImportModal
        open={showBulkImportModal}
        onOpenChange={setShowBulkImportModal}
        onImport={handleBulkImport}
      />

      <SmartImportModal
        open={showSmartImportModal}
        onOpenChange={setShowSmartImportModal}
        onImport={handleBulkImport}
        projectName={currentProject}
      />

      <GoogleSheetsImportModal
        open={showGoogleSheetsModal}
        onOpenChange={setShowGoogleSheetsModal}
        onImportComplete={fetchData}
        project={currentProject}
      />

      <PsgMailModal open={showPsgModal} onOpenChange={setShowPsgModal} onImported={fetchData} />
      <InboxModal
        open={showInboxModal}
        onOpenChange={setShowInboxModal}
        onRefreshData={fetchData}
      />

      <DoublonsModal
        open={showDoublonsModal}
        onOpenChange={setShowDoublonsModal}
        currentProject={currentProject}
        onRefreshData={fetchData}
      />

      <BulkValidateModal
        isOpen={showBulkValidateModal}
        onClose={() => setShowBulkValidateModal(false)}
        onSuccess={fetchData}
        project={currentProject}
      />

      <MatcherFilterModal
        isOpen={showMatcherFilterModal}
        onClose={() => setShowMatcherFilterModal(false)}
        onConfirm={handleRunMatchingFiltered}
        tickets={tickets}
        isLoading={matcherRunning}
      />

      <AntiSingleModal
        isOpen={showAntiSingleModal}
        onClose={() => setShowAntiSingleModal(false)}
        onSuccess={fetchData}
      />

      <AttribuerVenteModal
        open={showAttribuerVenteModal}
        onOpenChange={setShowAttribuerVenteModal}
        venteTicket={selectedVenteForAttribution}
        project={currentProject}
        onSuccess={fetchData}
        onSpecialAttribution={handleSpecialAttribution}
      />

      <SpecialAttributionModal
        open={showSpecialAttributionModal}
        onOpenChange={setShowSpecialAttributionModal}
        venteTicket={selectedVenteForAttribution}
        project={currentProject}
        onSuccess={fetchData}
      />

      <RefCheckModal
        open={showRefCheckModal}
        onOpenChange={setShowRefCheckModal}
        currentProject={currentProject}
      />

      {/* Proof Upload Modal */}
      <ProofUploadModal
        isOpen={showProofModal}
        onClose={() => {
          setShowProofModal(false);
          setProofTicket(null);
        }}
        ticket={proofTicket}
        onProofUploaded={(ticketId, proofUrl) => {
          // Update local state to reflect the proof
          setTickets(prev => prev.map(t => 
            t.id === ticketId ? { ...t, preuve_url: proofUrl } : t
          ));
        }}
      />

      {/* Proof Envoi Modal (for ACHAT tickets) */}
      <ProofEnvoiModal
        isOpen={showProofEnvoiModal}
        onClose={() => {
          setShowProofEnvoiModal(false);
          setProofEnvoiTicket(null);
        }}
        ticket={proofEnvoiTicket}
        onProofUpdated={(ticketId, preuveEnvoiUrl, type) => {
          // Update local state to reflect the proof envoi
          setTickets(prev => prev.map(t => 
            t.id === ticketId ? { ...t, preuve_envoi: preuveEnvoiUrl } : t
          ));
        }}
      />

      {/* Confirmation Dialog - Désattribuer Tout */}
      {showDesattribuerConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#1a1a1a] border border-red-500/50 rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-8 w-8 text-red-500" />
              <h3 className="text-xl font-bold text-white">ATTENTION</h3>
            </div>
            
            <p className="text-white text-lg mb-4">
              Vous allez <span className="text-red-400 font-bold">TOUT DÉSATTRIBUER</span>.
            </p>
            <p className="text-zinc-400 mb-2">
              Cette action va :
            </p>
            <ul className="text-zinc-300 text-sm mb-4 list-disc list-inside space-y-1">
              <li>Retirer les attributions de <strong>TOUS</strong> les billets ACHAT</li>
              <li>Effacer ref_vente, nom_client, mail_client</li>
              <li>Remettre le statut des VENTE à vide</li>
            </ul>
            <p className="text-emerald-400 text-sm mb-6">
              ✓ Un snapshot sera créé pour pouvoir revenir en arrière
            </p>
            
            <p className="text-white font-medium mb-4">Êtes-vous sûr ?</p>
            
            <div className="flex gap-3">
              <Button 
                onClick={() => setShowDesattribuerConfirm(false)}
                variant="outline"
                className="flex-1 border-zinc-600"
              >
                NON, Annuler
              </Button>
              <Button 
                onClick={confirmDesattribuerAll}
                className="flex-1 bg-red-600 hover:bg-red-500 text-white"
              >
                OUI, Désattribuer tout
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal - Restauration */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#1a1a1a] border border-[#D4AF37]/50 rounded-lg p-6 max-w-lg mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <History className="h-6 w-6 text-[#D4AF37]" />
                <h3 className="text-xl font-bold text-white">Historique des versions</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistoryModal(false)}
                className="text-zinc-400 hover:text-white h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <p className="text-zinc-400 text-sm mb-4">
              Restaurez une version antérieure de vos données.
              <span className="text-amber-400"> Attention : les données actuelles seront remplacées.</span>
            </p>
            
            {loadingSnapshots ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-[#D4AF37]" />
              </div>
            ) : snapshots.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Aucun snapshot disponible</p>
                <p className="text-xs mt-1">Les snapshots sont créés automatiquement lors d'actions importantes</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {snapshots.map((snap, idx) => {
                  const date = new Date(snap.created_at);
                  const now = new Date();
                  const diffMs = now - date;
                  const diffMins = Math.floor(diffMs / 60000);
                  const diffHours = Math.floor(diffMs / 3600000);
                  const diffDays = Math.floor(diffMs / 86400000);
                  
                  let timeAgo = '';
                  if (diffMins < 60) timeAgo = `${diffMins} min`;
                  else if (diffHours < 24) timeAgo = `${diffHours}h`;
                  else timeAgo = `${diffDays} jour(s)`;
                  
                  return (
                    <div 
                      key={snap.id}
                      className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700 hover:border-[#D4AF37]/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">{snap.reason}</p>
                          <p className="text-xs text-zinc-400">
                            {date.toLocaleString('fr-FR')} • {timeAgo} • {snap.ticket_count} tickets
                          </p>
                        </div>
                        <Button
                          onClick={() => handleRestoreSnapshot(snap.id)}
                          size="sm"
                          className="bg-[#D4AF37] hover:bg-[#D4B86A] text-black"
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Restaurer
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            <Button 
              onClick={() => setShowHistoryModal(false)}
              variant="outline" 
              className="w-full mt-4 border-zinc-700"
            >
              Fermer
            </Button>
          </div>
        </div>
      )}

      {/* Modal de confirmation changement de projet */}
      {showProjectChangeConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[200]">
          <div className="bg-zinc-900 border border-[#D4AF37]/30 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-full bg-amber-500/20">
                <AlertCircle className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Changer de projet ?</h3>
                <p className="text-zinc-400 text-sm">Cette action va charger un autre projet</p>
              </div>
            </div>
            
            <div className="bg-zinc-800 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-xs">Projet actuel</p>
                  <p className="text-white font-medium">{currentProject}</p>
                </div>
                <ArrowRight className="h-5 w-5 text-[#D4AF37]" />
                <div className="text-right">
                  <p className="text-zinc-400 text-xs">Nouveau projet</p>
                  <p className="text-[#D4AF37] font-bold">{pendingProjectChange}</p>
                </div>
              </div>
            </div>
            
            <p className="text-zinc-400 text-sm mb-4">
              Toutes les données affichées seront remplacées par celles du projet <span className="text-[#D4AF37] font-semibold">{pendingProjectChange}</span>.
            </p>
            
            <div className="flex gap-3">
              <Button
                onClick={cancelProjectChange}
                variant="outline"
                className="flex-1 border-zinc-600"
              >
                Annuler
              </Button>
              <Button
                onClick={confirmProjectChange}
                className="flex-1 bg-[#D4AF37] hover:bg-[#D4B86A] text-black font-bold"
              >
                Confirmer
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

// Wrap App with ErrorBoundary to catch all errors
const AppWithErrorBoundary = () => (
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);

export default AppWithErrorBoundary;
